import imageSrc from '../imageSrc';
import React from 'react';

/**
 * Magic Lamp — wheel preview
 *
 * Draws the wheel exactly as the tablet will, so an admin can see the effect of
 * a colour, a label or a picture before going live. Static: nothing here spins,
 * and nothing here decides anything.
 *
 * Kept deliberately in step with the kiosk's Wheel component. If the layout
 * changes there, it has to change here too — a preview that lies is worse than
 * no preview.
 *
 *   inner   the picture, floating on the slice colour
 *   outer   the label, curved along the wheel's edge
 *   centre  the SPIN hub
 */

const PALETTE = [
  '#8f1cab',
  '#ffb700',
  '#008ec3',
  '#6b21a8',
  '#e0a000',
  '#4bb4dc',
  '#4b0082',
  '#ffc933',
];

/**
 * Splits a label into up to `maxLines` roughly equal lines, on word boundaries.
 *
 * Balanced rather than greedy, so "Better luck next time" becomes
 * "Better luck" / "next time". Even lines look deliberate, and the shorter
 * longest-line is what lets the font size grow for the whole wheel.
 */
function splitBalanced(label, maxLines) {
  const words = String(label || '').split(/\s+/).filter(Boolean);
  if (maxLines <= 1 || words.length < 2) return [String(label || '')];

  const target = Math.ceil(words.length / maxLines);
  const lines = [];
  for (let i = 0; i < words.length; i += target) {
    lines.push(words.slice(i, i + target).join(' '));
  }
  return lines.slice(0, maxLines);
}

function slicePath(index, count, radius) {
  const angle = (2 * Math.PI) / count;
  const start = index * angle - Math.PI / 2;
  const end = start + angle;

  const x1 = radius + radius * Math.cos(start);
  const y1 = radius + radius * Math.sin(start);
  const x2 = radius + radius * Math.cos(end);
  const y2 = radius + radius * Math.sin(end);
  const largeArc = angle > Math.PI ? 1 : 0;

  return `M ${radius} ${radius} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

/**
 * An arc for a label to sit on. Slices in the lower half are drawn end-to-start
 * so their text stays the right way up.
 */
function arcTextPath(index, count, r, radius) {
  const angle = (2 * Math.PI) / count;
  const start = index * angle - Math.PI / 2;
  const end = start + angle;

  const midDeg = ((((index * 360) / count + 180 / count - 90) % 360) + 360) % 360;
  const lower = midDeg > 0 && midDeg < 180;

  const p = (a) => [radius + r * Math.cos(a), radius + r * Math.sin(a)];
  const [x1, y1] = p(lower ? end : start);
  const [x2, y2] = p(lower ? start : end);

  return `M ${x1} ${y1} A ${r} ${r} 0 0 ${lower ? 0 : 1} ${x2} ${y2}`;
}

/**
 * Matches the kiosk's three modes. Keep these in step with Wheel.jsx — the two
 * are separate apps, so nothing enforces it automatically.
 */
function layoutFor(mode) {
  const chosen = ['image-text', 'image', 'text'].includes(mode) ? mode : 'image-text';
  if (chosen === 'image') {
    return { showLabel: false, showImage: true, ringStart: null, bandInner: 0.2, bandOuter: 0.92 };
  }
  if (chosen === 'text') {
    return { showLabel: true, showImage: false, ringStart: 0.35, bandInner: null, bandOuter: null };
  }
  return { showLabel: true, showImage: true, ringStart: 0.7, bandInner: 0.34, bandOuter: 0.67 };
}

export default function WheelPreview({ segments = [], size = 280, displayMode }) {
  const count = segments.length || 1;
  const radius = size / 2;
  const sliceAngle = 360 / count;

  const { showLabel, showImage, ...layout } = layoutFor(displayMode);
  const trackStart = layout.ringStart ?? 0.7;
  const ringMid = radius * ((trackStart + 0.95) / 2);
  const ringDepth = radius * (0.95 - trackStart);
  const arcLength = ((2 * Math.PI * ringMid) / count) * 0.86;

  const CHAR_W = 0.55;
  const LINE_H = 1.05;

  // A slice with a picture has only the outer ring, so one line. One without
  // has the whole slice, so its label may wrap onto two.
  const perSegment = segments.map((segment) =>
    splitBalanced(segment.label, showImage && imageSrc(segment.imageKey) ? 1 : 2),
  );

  const longestLine = perSegment.reduce(
    (max, lines) => Math.max(max, ...lines.map((l) => l.length)),
    1,
  );
  const mostLines = perSegment.reduce((max, lines) => Math.max(max, lines.length), 1);

  const fontSize = Math.max(
    8,
    Math.min(
      arcLength / (longestLine * CHAR_W),
      (ringDepth * 0.9) / (mostLines * LINE_H),
      radius * 0.12,
      26,
    ),
  );
  const maxChars = Math.max(6, Math.floor(arcLength / (fontSize * CHAR_W)));

  // The picture sits in the inner area, clear of the hub and of the label ring.
  const bandInner = radius * (layout.bandInner ?? 0.34);
  const bandOuter = radius * (layout.bandOuter ?? 0.67);
  const bandMid = (bandInner + bandOuter) / 2;
  // Measured where the picture actually sits: a slice narrows towards the hub,
  // so sizing from the outer edge would push it past the dividing lines.
  const bandCover =
    Math.min(bandOuter - bandInner, 2 * bandMid * Math.sin(Math.PI / count)) * 0.9;

  if (!segments.length) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#94a3b8',
          fontSize: 13,
        }}
      >
        Add segments to preview
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: size, height: size + 20 }}>
      {/* Pointer, in the brand gold */}
      <svg
        width="26"
        height="34"
        viewBox="0 0 34 44"
        style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', zIndex: 2 }}
      >
        <path d="M17 42 L3 8 A16 16 0 0 1 31 8 Z" fill="#ffb700" />
        <circle cx="17" cy="13" r="5" fill="#3b0764" />
      </svg>

      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ marginTop: 18, borderRadius: '50%', boxShadow: '0 8px 24px rgba(59,7,100,0.18)' }}
      >
        <defs>
          <radialGradient id="preview-hub-face" cx="50%" cy="34%" r="72%">
            <stop offset="0%" stopColor="#ffd970" />
            <stop offset="55%" stopColor="#ffb700" />
            <stop offset="100%" stopColor="#e0a000" />
          </radialGradient>
        </defs>

        <circle cx={radius} cy={radius} r={radius} fill="#faf5ff" />

        {segments.map((segment, index) => {
          const color = segment.color || PALETTE[index % PALETTE.length];
          const mid = index * sliceAngle + sliceAngle / 2 - 90;
          const rad = (mid * Math.PI) / 180;
          const picture = showImage ? imageSrc(segment.imageKey) : null;

          const myLines = (perSegment[index] || ['']).map((line) =>
            line.length > maxChars ? `${line.slice(0, maxChars - 1)}…` : line,
          );
          const lineH = fontSize * LINE_H;

          // Turned to match the curved label: arc text sits on the tangent, a
          // quarter turn from the bisector. Lower-half slices turn the other
          // way, for the same reason their text is drawn backwards.
          const deg = ((mid % 360) + 360) % 360;
          const lower = deg > 0 && deg < 180;
          const imageAngle = lower ? mid - 90 : mid + 90;

          const bx = radius + bandMid * Math.cos(rad);
          const by = radius + bandMid * Math.sin(rad);

          return (
            <g key={segment.id || index} opacity={segment.isActive === false ? 0.45 : 1}>
              <path
                d={slicePath(index, count, radius)}
                fill={color}
                stroke="#ffffff"
                strokeWidth="2"
              />

              {showLabel && myLines.map((line, lineIndex) => {
                const r = ringMid + ((myLines.length - 1) / 2 - lineIndex) * lineH;
                const arcId = `preview-arc-${segment.id || index}-${lineIndex}`;
                return (
                  <g key={arcId}>
                    <defs>
                      <path id={arcId} d={arcTextPath(index, count, r, radius)} fill="none" />
                    </defs>
                    <text
                      fill="#ffffff"
                      fontSize={fontSize}
                      fontWeight="800"
                      style={{
                        pointerEvents: 'none',
                        paintOrder: 'stroke',
                        stroke: 'rgba(59,7,100,0.35)',
                        strokeWidth: fontSize * 0.12,
                      }}
                    >
                      <textPath
                        href={`#${arcId}`}
                        startOffset="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        {line}
                      </textPath>
                    </text>
                  </g>
                );
              })}

              {picture && (
                <image
                  href={picture}
                  x={bx - bandCover / 2}
                  y={by - bandCover / 2}
                  width={bandCover}
                  height={bandCover}
                  transform={`rotate(${imageAngle}, ${bx}, ${by})`}
                  // Whole picture, no clip and no backing panel: these are
                  // transparent cutouts and should float on the slice colour.
                  preserveAspectRatio="xMidYMid meet"
                />
              )}
            </g>
          );
        })}

        {/* The hub. Static here — the preview never turns — so the word can be
            drawn straight into the wheel rather than layered over it. */}
        <circle cx={radius} cy={radius} r={radius * 0.23} fill="#3b0764" />
        <circle cx={radius} cy={radius} r={radius * 0.205} fill="url(#preview-hub-face)" />
        <ellipse
          cx={radius}
          cy={radius - radius * 0.085}
          rx={radius * 0.14}
          ry={radius * 0.08}
          fill="#ffffff"
          opacity="0.22"
        />
        <text
          x={radius}
          y={radius}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#3b0764"
          fontSize={radius * 0.125}
          fontWeight="900"
          letterSpacing={radius * 0.012}
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          SPIN
        </text>
      </svg>
    </div>
  );
}
